import { useEffect, useMemo } from 'react';
import { createHook, createStore } from 'react-sweet-state';
import { LegionError } from '../../utils/legion';
import { useLazyService } from '../use-service';
import { requestPermissions, transformPermissions } from './utils';
const Store = createStore({
  initialState: {
    error: undefined,
    hasLoaded: false,
    isLoading: false,
    permissions: {
      canCreateTeams: true,
      canViewTeams: true,
      canAdminTeams: false
    }
  },
  actions: {
    setError: error => ({
      setState
    }) => {
      setState({
        hasLoaded: true,
        isLoading: false,
        error
      });
    },
    setLoading: isLoading => ({
      setState
    }) => {
      setState({
        isLoading
      });
    },
    setPermissions: permissions => ({
      setState
    }) => {
      setState({
        hasLoaded: true,
        isLoading: false,
        permissions: transformPermissions(permissions)
      });
    }
  },
  name: 'teams-permissions'
});
const useTeamsPermissionsStore = createHook(Store);
export const useTeamsPermissionsService = (scope, {
  enabled,
  initialState
} = {
  enabled: false,
  initialState: {}
}) => {
  // backwards compatibility allow orgId to be passed as a string
  const {
    orgId,
    cloudId
  } = typeof scope === 'string' || !scope ? {
    orgId: scope,
    cloudId: undefined
  } : scope;
  const [state, {
    setError,
    setLoading,
    setPermissions
  }] = useTeamsPermissionsStore();
  const [request, controller] = useMemo(() => requestPermissions({
    orgId,
    apiVersion: 'v4',
    siteId: cloudId !== null && cloudId !== void 0 ? cloudId : 'None'
  }), [orgId, cloudId]);
  const [getPermissions, {
    loading,
    error,
    data
  }] = useLazyService(request, controller, {
    ErrorHandler: LegionError,
    initialState
  });
  const isLoadingElsewhere = state.isLoading && !loading;
  const hasLoadedAnywhere = state.error || state.hasLoaded;
  const willSendRequest = orgId && enabled && !hasLoadedAnywhere && !isLoadingElsewhere;
  useEffect(() => {
    const fetchData = async () => {
      if (willSendRequest) {
        setLoading(true);
        await getPermissions();
      }
    };
    fetchData();
    return () => {
      setLoading(false);
    };
  }, [getPermissions, setLoading, willSendRequest]);
  useEffect(() => {
    if (data !== null && data !== void 0 && data.permissions) {
      setPermissions(data.permissions);
    }
    if (error) {
      setError(error);
    }
  }, [data, error, setPermissions, setError]);
  return {
    loading,
    data,
    ...state
  };
};